import type { Task } from "~skillBuilder/utils/types";

import { image, link, tip, title } from "../../contentBlocks";

export const PBeyondClass = {
  name: "Beyond the Classroom",
  uniqueName: "p-beyond-class",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "professionalism.beyond-class/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Beyond the Classroom", durationTimeText: "10 min" })}
<p>Growing as a substitute teacher means more than just managing a classroom—it's about building positive relationships with school staff, engaging in the school's culture, and actively pursuing opportunities for professional growth.</p>

<p>By embracing feedback, participating in continued development, and learning from each classroom experience, you strengthen your skills and set yourself up for long-term success.</p>
<div align="center">
<div style="max-width:250px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1758202463/skillbuilderAssets/optimized/handshake.jpg", alt: "Two people shaking hands." })}
</div>
</div>
            `,
          },
        },
      ],
    },
    // Step 2
    {
      nextButtonText: "Next",
      questions: [
        // Question 2.1
        {
          attribute: "professionalism.beyond-class/leaving",
          label: "Before you leave",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Always leave the classroom in better condition than you found it. Write a clear, detailed note to the teacher that includes what was taught, any challenges or behavior issues that came up, and any follow-up items the teacher should know about. Be sure the room is tidy and that all materials are returned to their proper places.
            `,
          },
        },
      ],
    },
    // Step 3
    {
      nextButtonText: "Next",
      questions: [
        // Question 3.1
        {
          attribute: "professionalism.beyond-class/mindset",
          label: "Foster a professional mindset",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Each day is an opportunity to grow. Approach every request with a commitment to professionalism by:

<ul style="margin-left: 16px;">
<li>🕒 Arriving on time</li>
<li>😊 Maintaining a positive attitude</li>
<li>🙋 Actively engaging with students</li>
<li>👋 Introducing yourself to staff and fellow teachers</li>
<li>📳 Keeping personal electronic devices out of sight</li>
<li>📝 Leaving thorough notes at the end of the day</li>
</ul>

Stay open to new strategies and uphold the school's values and expectations.
            `,
          },
        },
      ],
    },
    // Step 4
    {
      nextButtonText: "Next",
      questions: [
        // Question 4.1
        {
          attribute: "professionalism.beyond-class/pd",
          label: "Professional development",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Just as we encourage students to keep learning, the same principle applies to our own professional growth.

By continually refining your skills, you demonstrate a commitment to growth, adaptability, and staying current with educational best practices. While this is not a requirement, seeking opportunities for ongoing professional development can boost your confidence, reinforce professional behavior, and equip you to navigate diverse classroom environments with greater effectiveness.
            `,
          },
        },
      ],
    },
    // Step 5
    {
      nextButtonText: "Next",
      questions: [
        // Question 5.1
        {
          attribute: "professionalism.beyond-class/leave-feedback",
          label: "Engage by leaving school feedback",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Use Swing's school feedback feature to share your experience at a school once the request has ended. While not required, your insights help us work with our partners to continually improve substitute experiences.

${link({ text: "How do I leave feedback about a school?", url: "https://support.swingeducation.com/en/articles/11119973-how-do-i-leave-feedback-about-a-school" })}
            `,
          },
        },
      ],
    },
    // Step 6
    {
      nextButtonText: "Next",
      questions: [
        // Question 6.1
        {
          attribute: "professionalism.beyond-class/give-feedback",
          label: "Ask for feedback",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
When you're committed to maintaining high standards in the classroom, feedback from school administrators and fellow educators can be invaluable. Use it as a way to continually improve your practice!

<ul style="margin-left: 16px;">
<li>🌟 <strong>Grow Your Skills</strong> - Your abilities develop through effort, learning, and perseverance.</li>
<li>💡 <strong>Stay Open</strong> - Actively seeking feedback shows you are open to new ideas.</li>
<li>🚀 <strong>Turn Challenges into Opportunities</strong> - Feedback helps you see challenges as chances to grow and improve.</li>
</ul>
            `,
          },
        },
      ],
    },
    // Step 7
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 7.1
        {
          attribute: "professionalism.beyond-class/support",
          label: "Seek support",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
If you run into an issue during a request, let Swing's Support team know—we're here to help! Avoid discussing pay 💵 or job availability 📅 directly with the school, as those topics vary per request.

${tip({ text: "Reach out to Swing if anything goes wrong—we're here to help!" })}
            `,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
